import { useMemo, useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import BoardSVG from './BoardSVG';
import OverlayPortal from './OverlayPortal';
import Avatar from './Avatar';
import { shouldPlaySound } from './SoundToggle';
import { useViewport } from "../hooks/useViewport";

interface GameOverState {
  outcome: 'you' | 'opp' | 'tie';
  playerUsername: string;
  opponentUsername: string;
  yourScore: number;
  opponentScore: number;
  wager: number;
  currentBalance: number;
  gameState?: any; // Optional: frozen game state to show board
  tournamentInfo?: { // Optional: tournament match info
    tournamentId: number;
    matchId: number;
    tournamentName: string;
    userEliminated?: boolean; // True if player was eliminated from tournament
    tournamentCompleted?: boolean; // True if tournament is completed (winner)
    userFinalPlacement?: number; // Final placement (1st, 2nd, 3rd)
    userPrizeGlint?: number; // Prize glint amount
    userPrizeGear?: number; // Prize gear amount
    entryFeeGear?: number; // Entry fee in gear
    topPrizeGlint?: number; // Top prize glint amount
    topPrizeGear?: number; // Top prize gear amount
  } | null;
  nextMatchInfo?: { // Optional: next match info for auto-advancing (including tiebreakers)
    id: number;
    tournament_id: number;
    gid?: string; // Optional: game ID if backend already created the game (for tiebreakers)
    is_tiebreaker?: boolean; // True if this is a tiebreaker game
  } | null;
}

function calculateLevel(wager: number): number {
  const level = Math.log2(wager / 0.01) + 1;
  return Math.round(level);
}

export default function GameOverScreenV2() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as GameOverState | null;
  const isAndroidApp = typeof window !== 'undefined' && (window as any).Capacitor && (window as any).Capacitor.getPlatform() === 'android';
  const isIOSApp = typeof window !== 'undefined' && (window as any).Capacitor && (window as any).Capacitor.getPlatform && (window as any).Capacitor.getPlatform() === 'ios';
  const useWebAudioAPI = isAndroidApp || isIOSApp;

  const [fadeIn, setFadeIn] = useState(false);
  const [titleVisible, setTitleVisible] = useState(false);
  const [statsVisible, setStatsVisible] = useState(false);
  const gameOverAudioRef = useRef<HTMLAudioElement | null>(null);
  const gameOverSongRef = useRef<HTMLAudioElement | null>(null);
  const gameOverAudioStartedRef = useRef(false);
  const gameOverSongStartedRef = useRef(false);
  
  // Android: Web Audio API for GameOverSong (to avoid conflicts with game audio)
  const gameOverSongAudioContextRef = useRef<AudioContext | null>(null);
  const gameOverSongBufferRef = useRef<AudioBuffer | null>(null);
  const gameOverSongSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const gameOverSongGainRef = useRef<GainNode | null>(null);

  if (!state) {
    // Redirect if no state
    navigate('/play');
    return null;
  }

  const {
    outcome,
    playerUsername,
    opponentUsername,
    yourScore,
    opponentScore,
    wager,
    currentBalance,
    gameState,
    tournamentInfo,
    nextMatchInfo,
  } = state;

  useEffect(() => {
    // Prevent background scroll when overlay is open
    document.body.style.overflow = 'hidden';

    // Auto-navigate for tournament matches after a delay
    let tournamentTimer: ReturnType<typeof setTimeout> | null = null;
    if (tournamentInfo) {
      tournamentTimer = setTimeout(async () => {
        if (tournamentInfo.tournamentCompleted) {
          // Tournament completed - winner should see winner screen
          console.log('[GameOverScreenV2] Tournament completed, showing winner screen');
          sessionStorage.removeItem('current_tournament');
          sessionStorage.removeItem('tournament_match_starting');
          // Stay on game over screen longer for winner, then navigate to tournaments
          setTimeout(() => {
            navigate('/tournaments');
          }, 5000); // 5 seconds for winner to see their victory
          return;
        } else if (tournamentInfo.userEliminated) {
          sessionStorage.removeItem('current_tournament');
          sessionStorage.removeItem('tournament_match_starting');
          navigate('/tournaments');
        } else if (nextMatchInfo) {
          // Check if tournament is still active before navigating
          try {
            const API_BASE = (import.meta as any).env?.VITE_API_BASE || 'http://localhost:8100';
            const headers: HeadersInit = { "Content-Type": "application/json" };
            try {
              const { getValidGuestToken } = await import('../lib/guest-auth');
              const guestToken = await getValidGuestToken();
              if (guestToken) {
                headers["Authorization"] = `Bearer ${guestToken}`;
              }
            } catch {
              // Continue without token for registered users
            }
            
            const tournamentResponse = await fetch(`${API_BASE}/tournaments/${nextMatchInfo.tournament_id}`, {
              credentials: "include",
              headers,
            });
            
            if (tournamentResponse.ok) {
              const tournamentData = await tournamentResponse.json();
              // If tournament is completed, navigate to tournaments page instead
              if (tournamentData.status === "completed") {
                console.log('[GameOverScreenV2] Tournament is completed, navigating to tournaments page');
                sessionStorage.removeItem('current_tournament');
                sessionStorage.removeItem('tournament_match_starting');
                navigate('/tournaments');
                return;
              }
            }
          } catch (err) {
            console.error('[GameOverScreenV2] Failed to check tournament status:', err);
            // Continue with navigation if check fails
          }
          // For tiebreakers, reset game state before navigating
          if (nextMatchInfo.is_tiebreaker) {
            // Reset game state when navigating to tiebreaker
            // This ensures the new game loads cleanly without stale state
            console.log('[GameOverScreenV2] Navigating to tiebreaker game, resetting game state');
            // The resetGame() call will happen in the Play component when it loads the new game
          }
          
          if (nextMatchInfo.gid) {
            navigate(`/play?gid=${nextMatchInfo.gid}&tournament=${nextMatchInfo.tournament_id}&match=${nextMatchInfo.id}`);
          } else {
            navigate(`/play?tournament=${nextMatchInfo.tournament_id}&match=${nextMatchInfo.id}`);
          }
        } else {
          sessionStorage.removeItem('current_tournament');
          sessionStorage.removeItem('tournament_match_starting');
          navigate('/tournaments');
        }
      }, 5000);
    }

    // Set up GameOver.mp3 audio - use relative path like matchmaking audio (works on Android)
    const audio = new Audio('./GameOver.mp3');
    audio.preload = 'auto';
    audio.addEventListener('error', (e) => {
      console.error('[GameOver] GameOver.mp3 error:', e, audio.error, { src: audio.src });
    });
    gameOverAudioRef.current = audio;
    
    // Android & iOS: Set up GameOverSong.mp3 via Web Audio API (to avoid conflicts with game audio)
    if (useWebAudioAPI) {
      const loadGameOverSong = async () => {
        try {
          const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
          if (AudioContextClass) {
            const audioContext = new AudioContextClass();
            gameOverSongAudioContextRef.current = audioContext;
            
            const response = await fetch('./GameOverSong.mp3');
            const arrayBuffer = await response.arrayBuffer();
            const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
            gameOverSongBufferRef.current = audioBuffer;
          }
        } catch (err) {
          console.error('[GameOver] Failed to load GameOverSong via Web Audio API:', err);
        }
      };
      loadGameOverSong();
    } else {
      // Web: Set up GameOverSong.mp3 audio - use relative path like matchmaking audio
      const song = new Audio('./GameOverSong.mp3');
      song.preload = 'auto';
      song.addEventListener('error', (e) => {
        console.error('[GameOver] GameOverSong.mp3 error:', e, song.error, { src: song.src });
      });
      gameOverSongRef.current = song;
    }
    
    // Helper function to start GameOverSong via Web Audio API (Android & iOS)
    const startGameOverSongWebAudio = (
      audioContext: AudioContext,
      buffer: AudioBuffer
    ) => {
      try {
        // Stop existing source if any
        if (gameOverSongSourceRef.current) {
          try {
            gameOverSongSourceRef.current.stop();
            gameOverSongSourceRef.current.disconnect();
          } catch {}
          gameOverSongSourceRef.current = null;
        }
        
        // Create new source
        const source = audioContext.createBufferSource();
        const gain = audioContext.createGain();
        source.buffer = buffer;
        source.loop = true; // Loop the song
        gain.gain.value = 0.3; // Start at 30% volume (fades to 80%)
        source.connect(gain);
        gain.connect(audioContext.destination);
        
        const startTime = audioContext.currentTime;
        source.start(startTime);
        gameOverSongSourceRef.current = source;
        gameOverSongGainRef.current = gain;
        
        // Fade in over 500ms
        const fadeInInterval = setInterval(() => {
          if (gameOverSongGainRef.current) {
            const maxVolume = 0.75; // Max 75% on Android
            if (gameOverSongGainRef.current.gain.value < maxVolume) {
              gameOverSongGainRef.current.gain.value = Math.min(maxVolume, gameOverSongGainRef.current.gain.value + 0.1);
            } else {
              clearInterval(fadeInInterval);
            }
          }
        }, 50);
      } catch (err) {
        console.error('[GameOver] Failed to start GameOverSong Web Audio:', err);
      }
    };
    
    // Start GameOverSong.mp3 before GameOver.mp3 ends to avoid gap
    // Use timeupdate to detect when GameOver.mp3 is near the end
    let fadeInIntervalRef: ReturnType<typeof setInterval> | null = null;
    
    const handleTimeUpdate = () => {
      if (!audio.duration) return; // Duration not loaded yet
      
        // Start GameOverSong.mp3 0.8 seconds before GameOver.mp3 ends
        const timeUntilEnd = audio.duration - audio.currentTime;
        if (timeUntilEnd <= 0.8 && !gameOverSongStartedRef.current) {
          gameOverSongStartedRef.current = true;
          
          // Android & iOS: Use Web Audio API
          if (useWebAudioAPI) {
          const audioContext = gameOverSongAudioContextRef.current;
          const buffer = gameOverSongBufferRef.current;
          if (audioContext && buffer && shouldPlaySound()) {
            try {
              // Resume AudioContext if suspended
              if (audioContext.state === 'suspended') {
                audioContext.resume().then(() => {
                  startGameOverSongWebAudio(audioContext, buffer);
                }).catch(err => {
                  console.error('[GameOver] Failed to resume AudioContext:', err);
                });
              } else {
                startGameOverSongWebAudio(audioContext, buffer);
              }
            } catch (err) {
              console.error('[GameOver] Failed to start GameOverSong via Web Audio API:', err);
            }
          }
        } else {
          // Web: Use HTML5 Audio
          if (gameOverSongRef.current && shouldPlaySound()) {
            // Start at low volume and fade in to smooth transition
            const targetVolume = 0.3; // Start at 30%
            gameOverSongRef.current.volume = targetVolume;
            const playPromise = gameOverSongRef.current.play();
            if (playPromise !== undefined) {
              playPromise
                .then(() => {
                  // Fade in over 500ms
                  fadeInIntervalRef = setInterval(() => {
                    if (gameOverSongRef.current) {
                      const maxVolume = 1.0; // Max 100% on web
                      if (gameOverSongRef.current.volume < maxVolume) {
                        gameOverSongRef.current.volume = Math.min(maxVolume, gameOverSongRef.current.volume + 0.1);
                      } else {
                        if (fadeInIntervalRef) {
                          clearInterval(fadeInIntervalRef);
                          fadeInIntervalRef = null;
                        }
                      }
                    }
                  }, 50);
                })
                .catch(err => {
                  console.error('Error playing GameOverSong.mp3:', err);
                });
            }
          }
        }
        // Remove listener once we've started the song
        audio.removeEventListener('timeupdate', handleTimeUpdate);
      }
    };
    
    audio.addEventListener('timeupdate', handleTimeUpdate);
    
    // Fallback: if timeupdate doesn't fire or duration isn't available, start when GameOver.mp3 ends
    const handleGameOverEnded = () => {
      if (gameOverSongStartedRef.current) return;
      gameOverSongStartedRef.current = true;
      
      // Android & iOS: Use Web Audio API
      if (useWebAudioAPI) {
        const audioContext = gameOverSongAudioContextRef.current;
        const buffer = gameOverSongBufferRef.current;
        if (audioContext && buffer && shouldPlaySound()) {
          try {
            if (audioContext.state === 'suspended') {
              audioContext.resume().then(() => {
                startGameOverSongWebAudio(audioContext, buffer);
              }).catch(err => {
                console.error('[GameOver] Failed to resume AudioContext:', err);
              });
            } else {
              startGameOverSongWebAudio(audioContext, buffer);
            }
          } catch (err) {
            console.error('[GameOver] Failed to start GameOverSong via Web Audio API:', err);
          }
        }
      } else {
        // Web: Use HTML5 Audio
        if (gameOverSongRef.current && shouldPlaySound()) {
          gameOverSongRef.current.volume = 1;
          gameOverSongRef.current.play().catch(err => {
            console.error('Error playing GameOverSong.mp3:', err);
          });
        }
      }
    };
    
    audio.addEventListener('ended', handleGameOverEnded);

    // Staggered fade-in animations
    requestAnimationFrame(() => {
      setFadeIn(true);
      setTimeout(() => setTitleVisible(true), 50);
      setTimeout(() => setStatsVisible(true), 150);
    });
    
    const tryPlayGameOverAudio = (attempt: number = 0) => {
      const audio = gameOverAudioRef.current;
      if (!audio || !shouldPlaySound()) return;
      if (gameOverAudioStartedRef.current && !audio.paused) return;
      audio.currentTime = 0;
      audio
        .play()
        .then(() => { gameOverAudioStartedRef.current = true; })
        .catch(err => {
          if (attempt < 2) {
            setTimeout(() => tryPlayGameOverAudio(attempt + 1), 150);
          } else {
            console.error('Error playing GameOver.mp3:', err);
          }
        });
    };

    // Play audio once the component mounts (respect sound toggle)
    tryPlayGameOverAudio();

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        tryPlayGameOverAudio();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    // Cleanup: restore scroll on unmount and stop audio
    return () => {
      document.body.style.overflow = '';
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleGameOverEnded);
      document.removeEventListener('visibilitychange', handleVisibility);
      if (fadeInIntervalRef) {
        clearInterval(fadeInIntervalRef);
        fadeInIntervalRef = null;
      }
      if (tournamentTimer) {
        clearTimeout(tournamentTimer);
      }

      if (gameOverAudioRef.current) {
        try {
          gameOverAudioRef.current.pause();
          gameOverAudioRef.current.currentTime = 0;
        } catch {}
        gameOverAudioRef.current = null;
      }
      // Android & iOS: Cleanup Web Audio API
      if (useWebAudioAPI) {
        if (gameOverSongSourceRef.current) {
          try {
            gameOverSongSourceRef.current.stop();
            gameOverSongSourceRef.current.disconnect();
          } catch {}
          gameOverSongSourceRef.current = null;
        }
        if (gameOverSongGainRef.current) {
          try {
            gameOverSongGainRef.current.disconnect();
          } catch {}
          gameOverSongGainRef.current = null;
        }
        if (gameOverSongAudioContextRef.current) {
          try {
            gameOverSongAudioContextRef.current.close().catch(() => {});
          } catch {}
          gameOverSongAudioContextRef.current = null;
        }
      } else {
        // Web: Cleanup HTML5 Audio
        if (gameOverSongRef.current) {
          try {
            gameOverSongRef.current.pause();
            gameOverSongRef.current.currentTime = 0;
            gameOverSongRef.current.volume = 1; // Reset volume
          } catch {}
          gameOverSongRef.current = null;
        }
      }
    };
  }, [tournamentInfo, navigate]);

  // Android WebView can suspend audio on background; retry playback on resume/visibility
  useEffect(() => {
    if (!useWebAudioAPI) return;
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        if (gameOverAudioRef.current && shouldPlaySound()) {
          if (gameOverAudioRef.current.paused) {
            gameOverAudioRef.current.currentTime = 0;
            gameOverAudioRef.current.play().then(() => { gameOverAudioStartedRef.current = true; }).catch(err => console.error('Error replaying GameOver.mp3 on resume:', err));
          }
        }
        // Android & iOS: Resume AudioContext if suspended
        if (useWebAudioAPI) {
          const audioContext = gameOverSongAudioContextRef.current;
          if (audioContext && audioContext.state === 'suspended') {
            audioContext.resume().catch(() => {});
          }
          // If source stopped, restart it
          if (!gameOverSongSourceRef.current && gameOverSongStartedRef.current && shouldPlaySound()) {
            const buffer = gameOverSongBufferRef.current;
            if (audioContext && buffer) {
              startGameOverSongWebAudio(audioContext, buffer);
            }
          }
        } else {
          // Web: Resume HTML5 Audio
          if (gameOverSongRef.current && shouldPlaySound() && gameOverSongStartedRef.current && gameOverSongRef.current.paused) {
            gameOverSongRef.current.play().catch(err => console.error('Error replaying GameOverSong.mp3 on resume:', err));
          }
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [useWebAudioAPI]);

  const level = useMemo(() => calculateLevel(wager), [wager]);
  
  const viewport = useViewport({ mobileBreakpoint: 480, tabletBreakpoint: 640 });
  const isMobile = viewport.isMobile;
  const isTablet = viewport.isTablet;
    const isSmallHeight = viewport.height <= 420;
    const isAndroid = typeof window !== 'undefined' && (window as any).Capacitor && (window as any).Capacitor.getPlatform() === 'android';
    const userAgent = typeof navigator !== 'undefined' ? (navigator.userAgent || navigator.vendor || '').toLowerCase() : '';
    const isIOSDevice = /ipad|iphone|ipod/.test(userAgent);
  
  // Bulletproof iPad detection - exclude iPhone
  const isIOSPad = (() => {
    if (!isIOSApp && !isIOSDevice) return false;
    // Explicit iPad in user agent
    if (userAgent.includes('ipad')) return true;
    // If it's iPhone (not iPad), exclude it
    if (userAgent.includes('iphone') && !userAgent.includes('ipad')) return false;
    if (userAgent.includes('ipod')) return false;
    // Check viewport dimensions
    if (Math.max(viewport.width, viewport.height) >= 768) {
      // Additional check: if dimensions suggest iPhone, exclude it
      const maxDim = Math.max(viewport.width, viewport.height);
      const minDim = Math.min(viewport.width, viewport.height);
      // If both dimensions are less than 768, it's likely iPhone
      if (maxDim < 768 || minDim < 768) return false;
      return true;
    }
    return false;
  })();
  
  // Bulletproof iPhone detection
  const isIPhone = (() => {
    if (!isIOSApp && !isIOSDevice) return false;
    if (isIOSPad) return false;
    if (userAgent.includes('iphone') && !userAgent.includes('ipad')) return true;
    if (userAgent.includes('ipod')) return true;
    if (isIOSApp && !isIOSPad) {
      const maxDim = Math.max(viewport.width, viewport.height);
      const minDim = Math.min(viewport.width, viewport.height);
      if (maxDim < 768 || minDim < 768) return true;
    }
    return false;
  })();
  
  const determineHeadline = () => {
    if (tournamentInfo) {
      // Check for placement-specific messages (1st, 2nd, 3rd place)
      // Only show winner screen if there's a prize for that placement
      if (tournamentInfo.userFinalPlacement !== undefined) {
        const hasPrize = (tournamentInfo.userPrizeGlint && tournamentInfo.userPrizeGlint > 0) ||
                         (tournamentInfo.userPrizeGear && tournamentInfo.userPrizeGear > 0);
        
        if (hasPrize) {
          if (tournamentInfo.userFinalPlacement === 1) {
            return '🏆 Tournament Winner!';
          } else if (tournamentInfo.userFinalPlacement === 2) {
            return '🥈 2nd Place Winner!';
          } else if (tournamentInfo.userFinalPlacement === 3) {
            return '🥉 3rd Place Winner!';
          }
        }
        // If placement exists but no prize, fall through to eliminated screen
      }
      
      // CRITICAL: Check userEliminated BEFORE tournamentCompleted
      // If user is eliminated, they should see eliminated screen even if tournament is completed

      if (tournamentInfo.userEliminated) {
        return "You've Been Eliminated";
      }

      // Only show "Tournament Winner!" if tournament is completed AND user is NOT eliminated
      if (tournamentInfo.tournamentCompleted && !tournamentInfo.userEliminated) {
        return '🏆 Tournament Winner!';
      }

      // Check for tiebreaker in nextMatchInfo first (more reliable than outcome === 'tie')
      if (nextMatchInfo?.is_tiebreaker || outcome === 'tie') {
        return 'Tiebreaker Starting!';
      }
      if (outcome === 'you') {
        return 'Advancing to Next Round';
      }
      // outcome === 'opp' but not eliminated (should not happen often)
      return 'Tournament Match Complete';
    }

    // Non-tournament games
    return outcome === 'you'
      ? 'You Win!'
      : outcome === 'opp'
      ? 'You Lost!'
      : 'Tie Game!';
  };

  const outcomeText = determineHeadline();
  
  // Use site-appropriate brown color matching the theme (same for all outcomes)
  const outcomeColor = '#cd853f'; // Brighter brown matching site theme
  const textColorOnTan = '#4b2c10'; // Higher contrast for text on tan buttons
  
  // Determine summary text - tournament-specific or regular game
  const determineSummaryText = () => {
    if (tournamentInfo) {
      // Case 1-3: Tournament finished with placement and prize
      if (tournamentInfo.userFinalPlacement !== undefined) {
        const hasPrize = (tournamentInfo.userPrizeGlint && tournamentInfo.userPrizeGlint > 0) ||
                         (tournamentInfo.userPrizeGear && tournamentInfo.userPrizeGear > 0);
        if (hasPrize) {
          const glintText = tournamentInfo.userPrizeGlint ? `${tournamentInfo.userPrizeGlint} glint` : '';
          const gearText = tournamentInfo.userPrizeGear ? `${tournamentInfo.userPrizeGear} gear` : '';
          const prizeText = [glintText, gearText].filter(Boolean).join(' and ');
          
          if (tournamentInfo.userFinalPlacement === 1) {
            return `You won the tournament and earned ${prizeText}!`;
          } else if (tournamentInfo.userFinalPlacement === 2) {
            return `You placed 2nd and earned ${prizeText}!`;
          } else if (tournamentInfo.userFinalPlacement === 3) {
            return `You placed 3rd and earned ${prizeText}!`;
          }
        }
      }
      
      // Case 4: Eliminated
      if (tournamentInfo.userEliminated) {
        return 'Your tournament run has ended.';
      }
      
      // Case 5: Advancing to next round
      if (outcome === 'you' && !tournamentInfo.tournamentCompleted) {
        return 'Great win! Your next match is starting soon.';
      }
      
      // Case 6: Tiebreaker
      if (nextMatchInfo?.is_tiebreaker || outcome === 'tie') {
        return 'This game was a tie — a tiebreaker match is required.';
      }
      
      // Case 7: Fallback
      return 'Waiting for other matches to complete…';
    }
    
    // Regular games - keep existing logic unchanged
    const scoreDiff = Math.abs(yourScore - opponentScore);
    return outcome === 'you' 
      ? `You win by ${scoreDiff} lbs!`
      : outcome === 'opp'
      ? `You lost by ${scoreDiff} lbs`
      : 'Tied game!';
  };

  const diffText = determineSummaryText();

  const handlePlayAgain = () => {
    // Stop the game over song when user clicks Play Again
    // Android & iOS: Stop Web Audio API
    if (useWebAudioAPI) {
      if (gameOverSongSourceRef.current) {
        try {
          gameOverSongSourceRef.current.stop();
          gameOverSongSourceRef.current.disconnect();
        } catch {}
        gameOverSongSourceRef.current = null;
      }
    } else {
      // Web: Stop HTML5 Audio
      if (gameOverSongRef.current) {
        try {
          gameOverSongRef.current.pause();
          gameOverSongRef.current.currentTime = 0;
        } catch {}
      }
    }
    navigate('/play?from=gameover');
  };

  const useBottomSheet = isMobile || isSmallHeight;

  // iPad-specific layout (leave other platforms unchanged)
  if (isIOSPad) {
    const padOutcomeFont = 'clamp(32px,5vw,72px)';
    const padDiffFont = 'clamp(18px,2.6vw,28px)';
    const padAvatar = Math.max(100, Math.min(130, Math.round(Math.max(viewport.width, viewport.height) * 0.08)));
    const padNameFont = 'clamp(14px,2.3vw,24px)';
    const padScoreFont = 'clamp(22px,3.4vw,40px)';

    return (
      <OverlayPortal>
        <div 
          className="fixed inset-0 w-screen z-[1000]" 
          style={{ 
            background: 'rgba(0, 0, 0, 0.85)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            height: '100dvh',
            minHeight: '100svh',
            overscrollBehavior: 'contain'
          }}
        >
          <div 
            className="absolute inset-0 flex justify-center items-center"
            style={{ transform: 'translateY(12vh)' }}
          >
            <div className="w-full h-full max-w-none bg-transparent shadow-none pointer-events-auto">
              <div className="relative w-full flex flex-col items-center"
                style={{ 
                  paddingTop: '1.25rem',
                  paddingBottom: '2.5rem',
                  paddingLeft: '1.25rem',
                  paddingRight: '1.25rem',
                  gap: '1.25rem'
                }}>
                {/* Outcome */}
                <div className="text-center flex-shrink-0 pt-0 pb-0 transition-opacity duration-300 relative" style={{ opacity: titleVisible ? 1 : 0 }}>
                  <div 
                    className="font-black tracking-wide relative z-10"
                    style={{ color: outcomeColor, fontSize: padOutcomeFont, marginBottom: '0.75rem' }}
                  >
                    {outcomeText}
                  </div>
                  <div className="italic opacity-90 relative z-10" style={{ color: outcomeColor, fontSize: padDiffFont }}>
                    {diffText}
                  </div>
                </div>

                {/* Names + scores */}
                <div className="flex flex-col items-center gap-5 w-full max-w-[92%] px-1.5 flex-shrink-0" style={{ marginTop: '0.5rem' }}>
                  <div className="flex flex-row items-center justify-center gap-4 w-full">
                    <div className="flex flex-col items-center">
                      <Avatar name={playerUsername} size={padAvatar} className="mb-2" />
                      <div className="rounded-2xl shadow-sm border-2"
                        style={{
                          background: "rgba(245, 214, 164, 0.88)",
                          borderColor: "#7b4a2a",
                          backdropFilter: "blur(1px)",
                          padding: '0.9rem 2.1rem',
                          marginBottom: '0.8rem',
                        }}>
                        <div className="font-extrabold leading-none" style={{ color: '#3e2615', fontSize: padNameFont }}>
                          {playerUsername}
                        </div>
                      </div>
                      <div className="font-semibold" style={{ color: outcomeColor, fontSize: padScoreFont, marginTop: '0.5rem' }}>
                        {yourScore} lbs
                      </div>
                    </div>

                    <div className="flex items-center" style={{ height: 'fit-content', paddingTop: '0' }}>
                      <div className="font-bold text-2xl" style={{ color: outcomeColor }}>vs.</div>
                    </div>

                    <div className="flex flex-col items-center">
                      <Avatar name={opponentUsername} size={padAvatar} className="mb-2" />
                      <div className="rounded-2xl shadow-sm border-2"
                        style={{
                          background: "rgba(245, 214, 164, 0.88)",
                          borderColor: "#7b4a2a",
                          backdropFilter: "blur(1px)",
                          padding: '0.9rem 2.1rem',
                          marginBottom: '0.8rem',
                        }}>
                        <div className="font-extrabold leading-none" style={{ color: '#3e2615', fontSize: padNameFont }}>
                          {opponentUsername}{(["Lump", "Ferris", "Magnus"].includes(opponentUsername)) && " [AI]"}
                        </div>
                      </div>
                      <div className="font-semibold" style={{ color: outcomeColor, fontSize: padScoreFont, marginTop: '0.5rem' }}>
                        {opponentScore} lbs
                      </div>
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className="flex items-center justify-center w-full px-3 flex-shrink-0" style={{ marginTop: '0.75rem' }}>
                  <div className="w-[85%] h-[2px] rounded-full" style={{
                    background: 'linear-gradient(to right, transparent, #8b6542 20%, #8b6542 80%, transparent)',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                  }} />
                </div>

                {/* Stats row */}
                <div className="flex flex-row items-center justify-between w-full max-w-2xl px-3 gap-4 flex-shrink-0 transition-opacity duration-300"
                  style={{ opacity: statsVisible ? 1 : 0 }}>
                  <div className="inline-flex items-center justify-center md:justify-start px-3 py-1 rounded-full border-2 flex-shrink-0" style={{
                    background: 'rgba(245, 214, 164, 0.88)',
                    borderColor: '#8b6542',
                    backdropFilter: 'blur(4px)'
                  }}>
                    <span className="font-bold" style={{ color: '#3e2615', fontSize: '1.1rem' }}>Level {level}</span>
                  </div>
                  
                  <div className="flex flex-col items-center gap-0 md:gap-1">
                    <div className="text-base font-semibold text-center" style={{ color: outcomeColor }}>
                      Glint wagered
                    </div>
                    <div className="text-xl font-bold" style={{ color: outcomeColor }}>
                      ✧{wager.toFixed(2)}
                    </div>
                  </div>
                  
                  <div className="flex flex-col items-center md:items-end gap-0 md:gap-1">
                    <div className="text-base font-semibold text-center md:text-right" style={{ color: outcomeColor }}>
                      Current balance
                    </div>
                    <div className="text-xl font-bold" style={{ color: outcomeColor }}>
                      ✧{currentBalance.toFixed(2)}
                    </div>
                  </div>
                </div>

                {/* Bottom Divider */}
                <div className="flex items-center justify-center w-full px-3 flex-shrink-0" style={{ marginTop: '0.75rem' }}>
                  <div className="w-[85%] h-[2px] rounded-full" style={{
                    background: 'linear-gradient(to right, transparent, #8b6542 20%, #8b6542 80%, transparent)',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                  }} />
                </div>

                {/* Play Again button */}
                {!tournamentInfo && (
                  <div className="flex justify-center flex-shrink-0 mt-auto" style={{ paddingTop: '1rem', paddingBottom: '0.5rem' }}>
                    <button
                      onClick={handlePlayAgain}
                      className="px-6 py-3 rounded-xl border-2 font-bold text-lg transition-all duration-200 hover:scale-105 active:scale-95"
                      style={{
                        background: 'linear-gradient(to bottom, #f7b267 0%, #f4a261 100%)',
                        borderColor: '#8b6542',
                        color: '#3e2615',
                        boxShadow: '0 4px 0 #8b6542, 0 6px 12px rgba(0,0,0,0.3)',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px) scale(1.05)';
                        e.currentTarget.style.boxShadow = '0 6px 0 #8b6542, 0 8px 16px rgba(0,0,0,0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0) scale(1)';
                        e.currentTarget.style.boxShadow = '0 4px 0 #8b6542, 0 6px 12px rgba(0,0,0,0.3)';
                      }}
                    >
                      Play Again
                    </button>
                  </div>
                )}

                {tournamentInfo && (
                  <div className="flex justify-center flex-shrink-0" style={{ paddingTop: '1rem', paddingBottom: '0.5rem' }}>
                    <div className="text-center text-sm md:text-base" style={{ color: outcomeColor }}>
                      {(() => {
                        if (tournamentInfo.userEliminated || 
                            (tournamentInfo.userFinalPlacement !== undefined) ||
                            (outcome === 'opp' && !tournamentInfo.userEliminated && !tournamentInfo.tournamentCompleted)) {
                          return <div className="text-sm opacity-90">Returning to tournament lobby…</div>;
                        } else if (outcome === 'you' && !tournamentInfo.tournamentCompleted) {
                          return <div className="text-sm opacity-90">Preparing your next match…</div>;
                        } else if (nextMatchInfo?.is_tiebreaker || outcome === 'tie') {
                          return <div className="text-sm opacity-90">Loading your tiebreaker match…</div>;
                        } else {
                          return <div className="text-sm opacity-90">Returning to tournament lobby…</div>;
                        }
                      })()}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </OverlayPortal>
    );
  }

  return (
    <OverlayPortal>
      {/* Full-viewport layer; dvh handles mobile URL bars and notches */}
      <div 
        className="fixed inset-0 w-screen z-[1000]" 
        style={{ 
          background: '#fffaeb',
          height: '100dvh',
          minHeight: '100svh',
          // @ts-ignore - CSS custom property for older browsers
          ['--minvh' as any]: '100vh',
          overscrollBehavior: 'contain'
        }}
      >
        {/* Gameboard in background - frozen */}
        {gameState && (
          <div 
            className="absolute inset-0 opacity-60 pointer-events-none flex justify-center" 
            style={{
              alignItems: useBottomSheet ? 'center' : 'flex-start',
              paddingTop: useBottomSheet ? '0' : 'clamp(80px, 8vh, 120px)'
            }}
          >
            <div className="w-full max-w-4xl" style={{ aspectRatio: '16 / 9' }}>
              <BoardSVG 
                s={gameState} 
                onPlay={() => {}} // Disabled
                onNextRound={() => {}} // Disabled
                playerUsername={gameState?.yourUsername || playerUsername}
                opponentUsername={
                  gameState?.opponentUsername ||
                  opponentUsername
                }
              />
            </div>
          </div>
        )}

        {/* Dimmer overlay */}
        <div
          className="absolute inset-0 transition-opacity duration-300"
          style={{ 
            background: 'rgba(0, 0, 0, 0.75)',
            opacity: fadeIn ? 1 : 0
          }}
        />

        {/* Content */}
        <div 
          className="absolute inset-0 flex justify-center" 
          style={{ 
            padding: useBottomSheet ? '0' : '0.5rem 0.75rem',
            alignItems: useBottomSheet ? 'center' : 'flex-start',
            paddingTop: useBottomSheet ? '0' : 'clamp(80px, 8vh, 120px)'
          }}
        >
          <div 
            className={[
              useBottomSheet
                ? "fixed top-0 bottom-0 inset-x-0 mx-auto rounded-none"
                : "relative rounded-2xl",
              "w-full max-w-4xl bg-black/35 backdrop-blur-md shadow-[0_20px_60px_rgba(0,0,0,0.6)] transition-all duration-300 pointer-events-auto"
            ].join(' ')}
            style={{ 
              opacity: fadeIn ? 1 : 0,
              transform: fadeIn ? 'scale(1)' : 'scale(0.96)',
              maxHeight: useBottomSheet ? '100dvh' : '90dvh',
              overflowY: 'auto',
              paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
              paddingTop: useBottomSheet ? 'env(safe-area-inset-top, 0px)' : '0'
            }}
          >
          
          <div className="relative w-full flex flex-col items-center" style={{ 
            paddingTop: useBottomSheet ? '0.25rem' : (isAndroid || isMobile ? '0.25rem' : 'clamp(0.5rem,2vh,1rem)'),
            paddingBottom: useBottomSheet ? 'clamp(0.5rem,2vh,1rem)' : (isAndroid || isMobile ? '0.25rem' : 'clamp(1.5rem,4vh,3rem)'),
            paddingLeft: isAndroid || isMobile ? '0.375rem' : 'clamp(0.5rem,2vh,1rem)',
            paddingRight: isAndroid || isMobile ? '0.375rem' : 'clamp(0.5rem,2vh,1rem)',
            gap: isAndroid || isMobile ? '0.125rem' : '0.5rem'
          }}>
            {/* Outcome - centered, moved up to be over the game */}
            <div className="text-center flex-shrink-0 pt-0 pb-0 transition-opacity duration-300 relative" style={{ opacity: titleVisible ? 1 : 0, paddingTop: isAndroid ? '1rem' : (isMobile ? '0.5rem' : '0') }}>
              <div 
                className={`${isAndroid || isMobile ? 'text-lg' : 'text-[clamp(16px,2.5vw,28px)]'} md:text-[clamp(28px,5vw,64px)] font-black tracking-wide relative z-10`}
                style={{ 
                  color: outcomeColor
                }}
              >
                {outcomeText}
              </div>
              {/* Difference summary */}
              <div className={`${isAndroid || isMobile ? 'text-[10px]' : 'text-xs'} md:text-base italic mt-0 md:mt-1 opacity-90 relative z-10`} style={{ color: outcomeColor }}>
                {diffText}
              </div>
            </div>

            {/* Middle content - reordered: Names first, then scores below */}
            <div className="flex flex-col items-center gap-0.5 md:gap-2 w-full max-w-[98%] md:max-w-[90%] px-0.5 md:px-0 flex-shrink-0" style={{ marginTop: isAndroid || isMobile ? '0.125rem' : '0' }}>
              {/* Names row - FIRST */}
              <div className="flex flex-col items-center gap-0.75 md:gap-2 flex-shrink-0 w-full">
                <div className="flex flex-row items-center justify-center gap-0.75 md:gap-3 w-full">
                  <div className="flex flex-col items-center">
                    {/* Avatar above username */}
                    <Avatar name={playerUsername} size={isAndroid ? 60 : (isMobile ? 48 : 72)} className={isAndroid ? "mb-0.5" : (isMobile ? "mb-0.5" : "mb-1 md:mb-2")} />
                    <div
                      className={`${isAndroid ? 'rounded-lg' : (isMobile ? 'rounded-lg' : 'rounded-xl md:rounded-2xl')} shadow-sm border-2`}
                      style={{
                        background: "rgba(245, 214, 164, 0.88)",
                        borderColor: "#7b4a2a",
                        backdropFilter: "blur(1px)",
                        padding: isAndroid ? '0.25rem 0.5rem' : (isMobile ? '0.125rem 0.375rem' : '0.5rem 1.5rem'),
                        marginBottom: isAndroid ? '0.5rem' : (isMobile ? '0.125rem' : '0.5rem'),
                      }}
                    >
                      <div 
                        className="font-extrabold leading-none" 
                        style={{ 
                          color: textColorOnTan,
                          fontSize: isAndroid ? '12px' : (isMobile ? '9px' : 'clamp(12px,2vw,20px)')
                        }}
                      >
                        {playerUsername}
                      </div>
                    </div>
                    {/* Score centered under username */}
                    <div className={`font-semibold ${isAndroid || isMobile ? 'text-xs' : 'text-[clamp(14px,2.5vw,24px)]'} md:text-[clamp(20px,4vw,36px)] mt-0.5 md:mt-1`} style={{ color: outcomeColor }}>
                      {yourScore} lbs
                    </div>
                  </div>
                  
                  <div className="flex items-center" style={{ height: 'fit-content', paddingTop: '0' }}>
                    <div className={`font-bold ${isAndroid || isMobile ? 'text-xs' : 'text-sm'} md:text-xl`} style={{ color: outcomeColor }}>vs.</div>
                  </div>
                  
                  <div className="flex flex-col items-center">
                    {/* Avatar above username */}
                    <Avatar name={opponentUsername} size={isAndroid ? 60 : (isMobile ? 48 : 72)} className={isAndroid ? "mb-0.5" : (isMobile ? "mb-0.5" : "mb-1 md:mb-2")} />
                    <div
                      className={`${isAndroid ? 'rounded-lg' : (isMobile ? 'rounded-lg' : 'rounded-xl md:rounded-2xl')} shadow-sm border-2`}
                      style={{
                        background: "rgba(245, 214, 164, 0.88)",
                        borderColor: "#7b4a2a",
                        backdropFilter: "blur(1px)",
                        padding: isAndroid ? '0.25rem 0.5rem' : (isMobile ? '0.125rem 0.375rem' : '0.5rem 1.5rem'),
                        marginBottom: isAndroid ? '0.5rem' : (isMobile ? '0.125rem' : '0.5rem'),
                      }}
                    >
                      <div 
                        className="font-extrabold leading-none" 
                        style={{ 
                          color: textColorOnTan,
                          fontSize: isAndroid ? '12px' : (isMobile ? '9px' : 'clamp(12px,2vw,20px)')
                        }}
                      >
                        {opponentUsername}{(["Lump", "Ferris", "Magnus"].includes(opponentUsername)) && " [AI]"}
                      </div>
                    </div>
                    {/* Score centered under username */}
                    <div className={`font-semibold ${isAndroid || isMobile ? 'text-xs' : 'text-[clamp(14px,2.5vw,24px)]'} md:text-[clamp(20px,4vw,36px)] mt-0.5 md:mt-1`} style={{ color: outcomeColor }}>
                      {opponentScore} lbs
                    </div>
                  </div>
                </div>
              </div>

              {/* First Divider - not full width */}
              <div className="flex items-center justify-center w-full px-1.5 md:px-4 flex-shrink-0" style={{ marginTop: isAndroid || isMobile ? '0.125rem' : '0.5rem' }}>
                <div 
                  className="w-[80%] h-[1.5px] md:h-[2px] rounded-full"
                  style={{
                    background: 'linear-gradient(to right, transparent, #8b6542 20%, #8b6542 80%, transparent)',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                  }}
                />
              </div>

              {/* Game details - all three in a row, responsive sizing */}
              <div className={`flex flex-row items-center justify-between w-full max-w-lg ${isAndroid || isMobile ? 'px-1 gap-0.5' : 'px-1.5 md:px-4 gap-1'} md:gap-3 flex-shrink-0 transition-opacity duration-300`} style={{ opacity: statsVisible ? 1 : 0 }}>
                {tournamentInfo ? (
                  <>
                    {/* Left - Entry Fee */}
                    <div className="flex flex-col items-center gap-0 md:gap-1">
                      <div className={`${isAndroid || isMobile ? 'text-[8px]' : 'text-[10px]'} md:text-sm font-semibold text-center`} style={{ color: outcomeColor }}>
                        Entry Fee
                      </div>
                      <div className={`${isAndroid || isMobile ? 'text-[9px]' : 'text-xs'} md:text-lg font-bold`} style={{ color: outcomeColor }}>
                        {tournamentInfo.entryFeeGear !== undefined ? `${tournamentInfo.entryFeeGear} Gear` : '—'}
                      </div>
                    </div>
                    
                    {/* Center - Your Prize */}
                    <div className="flex flex-col items-center gap-0 md:gap-1">
                      <div className={`${isAndroid || isMobile ? 'text-[8px]' : 'text-[10px]'} md:text-sm font-semibold text-center`} style={{ color: outcomeColor }}>
                        Your Prize
                      </div>
                      <div className={`${isAndroid || isMobile ? 'text-[9px]' : 'text-xs'} md:text-lg font-bold`} style={{ color: outcomeColor }}>
                        {(() => {
                          const hasPrize = (tournamentInfo.userPrizeGlint && tournamentInfo.userPrizeGlint > 0) ||
                                           (tournamentInfo.userPrizeGear && tournamentInfo.userPrizeGear > 0);
                          if (!hasPrize) return '—';
                          const glintText = tournamentInfo.userPrizeGlint ? `✧${tournamentInfo.userPrizeGlint}` : '';
                          const gearText = tournamentInfo.userPrizeGear ? `${tournamentInfo.userPrizeGear}G` : '';
                          return [glintText, gearText].filter(Boolean).join(' + ');
                        })()}
                      </div>
                    </div>
                    
                    {/* Right - Top Prize */}
                    <div className="flex flex-col items-center md:items-end gap-0 md:gap-1">
                      <div className={`${isAndroid || isMobile ? 'text-[8px]' : 'text-[10px]'} md:text-sm font-semibold text-center md:text-right`} style={{ color: outcomeColor }}>
                        Top Prize
                      </div>
                      <div className={`${isAndroid || isMobile ? 'text-[9px]' : 'text-xs'} md:text-lg font-bold`} style={{ color: outcomeColor }}>
                        {(() => {
                          if (tournamentInfo.topPrizeGlint === undefined && tournamentInfo.topPrizeGear === undefined) return '—';
                          const glintText = tournamentInfo.topPrizeGlint ? `✧${tournamentInfo.topPrizeGlint}` : '';
                          const gearText = tournamentInfo.topPrizeGear ? `${tournamentInfo.topPrizeGear}G` : '';
                          return [glintText, gearText].filter(Boolean).join(' + ');
                        })()}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Regular games - keep existing layout */}
                    <div className={`inline-flex items-center justify-center md:justify-start ${isAndroid || isMobile ? 'px-1.5 py-0.5' : 'px-2 py-0.5'} md:px-4 md:py-2 rounded-full border-2 flex-shrink-0`} style={{
                      background: 'rgba(245, 214, 164, 0.88)',
                      borderColor: '#8b6542',
                      backdropFilter: 'blur(4px)'
                    }}>
                      <span className={`${isAndroid || isMobile ? 'text-[8px]' : 'text-[10px]'} md:text-base font-bold`} style={{ color: textColorOnTan }}>Level {level}</span>
                    </div>
                    
                    <div className="flex flex-col items-center gap-0 md:gap-1">
                      <div className={`${isAndroid || isMobile ? 'text-[8px]' : 'text-[10px]'} md:text-sm font-semibold text-center`} style={{ color: outcomeColor }}>
                        Glint wagered
                      </div>
                      <div className={`${isAndroid || isMobile ? 'text-[9px]' : 'text-xs'} md:text-lg font-bold`} style={{ color: outcomeColor }}>
                        ✧{wager.toFixed(2)}
                      </div>
                    </div>
                    
                    <div className="flex flex-col items-center md:items-end gap-0 md:gap-1">
                      <div className={`${isAndroid || isMobile ? 'text-[8px]' : 'text-[10px]'} md:text-sm font-semibold text-center md:text-right`} style={{ color: outcomeColor }}>
                        Current balance
                      </div>
                      <div className={`${isAndroid || isMobile ? 'text-[9px]' : 'text-xs'} md:text-lg font-bold`} style={{ color: outcomeColor }}>
                        ✧{currentBalance.toFixed(2)}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Second Divider - not full width */}
              <div className="flex items-center justify-center w-full px-1.5 md:px-4 flex-shrink-0" style={{ marginTop: isAndroid || isMobile ? '0.125rem' : '0.5rem' }}>
                <div 
                  className="w-[80%] h-[1.5px] md:h-[2px] rounded-full"
                  style={{
                    background: 'linear-gradient(to right, transparent, #8b6542 20%, #8b6542 80%, transparent)',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                  }}
                />
              </div>
            </div>

            {/* Play Again button - hidden for tournament matches */}
            {!tournamentInfo && (
              <div className="flex justify-center flex-shrink-0 mt-auto" style={{ paddingTop: isAndroid || isMobile ? '0.125rem' : '1rem', paddingBottom: isAndroid || isMobile ? '0.125rem' : '0.5rem' }}>
                <button
                  onClick={handlePlayAgain}
                  className={`${isAndroid || isMobile ? 'px-3 py-1 text-[10px]' : 'px-4 py-1.5'} md:px-6 md:py-2.5 rounded-lg md:rounded-xl border-2 font-bold ${isAndroid || isMobile ? '' : 'text-[11px]'} md:text-sm transition-all duration-200 hover:scale-105 active:scale-95`}
                  style={{
                    background: 'linear-gradient(to bottom, #f7b267 0%, #f4a261 100%)',
                    borderColor: '#8b6542',
                    color: textColorOnTan,
                    boxShadow: '0 4px 0 #8b6542, 0 6px 12px rgba(0,0,0,0.3)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px) scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 6px 0 #8b6542, 0 8px 16px rgba(0,0,0,0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    e.currentTarget.style.boxShadow = '0 4px 0 #8b6542, 0 6px 12px rgba(0,0,0,0.3)';
                  }}
                >
                  Play Again
                </button>
              </div>
            )}
            {tournamentInfo && (
              <div className="flex justify-center flex-shrink-0" style={{ paddingTop: isAndroid || isMobile ? '0.125rem' : '1rem', paddingBottom: isAndroid || isMobile ? '0.125rem' : '0.5rem' }}>
                <div className={`text-center ${isAndroid || isMobile ? 'text-[10px]' : 'text-sm'} md:text-base`} style={{ color: outcomeColor }}>

                  {/* Determine footer text based on tournament state */}
                  {(() => {
                    // Case 1-3, 4, 7: Returning to tournament lobby
                    if (tournamentInfo.userEliminated || 
                        (tournamentInfo.userFinalPlacement !== undefined) ||
                        (outcome === 'opp' && !tournamentInfo.userEliminated && !tournamentInfo.tournamentCompleted)) {
                      return <div className="text-xs md:text-sm opacity-90">Returning to tournament lobby…</div>;
                    }
                    // Case 5: Preparing next match
                    else if (outcome === 'you' && !tournamentInfo.tournamentCompleted) {
                      return <div className="text-xs md:text-sm opacity-90">Preparing your next match…</div>;
                    }
                    // Case 6: Loading tiebreaker
                    else if (nextMatchInfo?.is_tiebreaker || outcome === 'tie') {
                      return <div className="text-xs md:text-sm opacity-90">Loading your tiebreaker match…</div>;
                    }
                    // Default fallback
                    else {
                      return <div className="text-xs md:text-sm opacity-90">Returning to tournament lobby…</div>;
                    }
                  })()}
                </div>
              </div>
            )}
          </div>
        </div>
        </div>
      </div>
    </OverlayPortal>
  );
}
